import parseISO from 'date-fns/parseISO';
import * as React from 'react';
import {Helmet} from 'react-helmet-async';
import {useTranslation} from 'react-i18next';
import {graphql, PageProps} from 'gatsby';
import {withPrismicPreview} from 'gatsby-plugin-prismic-previews';

import A11y from '@src/components/A11y';
import ArticleHero from '@src/components/Hero/ArticleHero';
import ArticleContent from '@src/components/ArticleContent';
import ArticleSidebar from '@src/components/ArticleSidebar';
import ArticleSidebarList from '@src/components/ArticleSidebar/List';
import ArticleSidebarListItem from '@src/components/ArticleSidebar/List/Item';
import PageLayout from '@src/components/PageLayout';
import PageSection from '@src/components/PageSection';
import PageTheme from '@src/components/PageTheme';

import * as styles from './article.module.scss';

import type {ArticleQueryQuery} from '@graphql-types';
import type {Translations} from '@src/i18n';

type Resource =
  ArticleQueryQuery['prismicArticle']['data']['related_resources'][0]['resource'];

interface ResourceWithData extends Resource {
  document: Extract<Resource['document'], {data: unknown}>;
}

interface RelatedResourceWithData {
  resource: ResourceWithData;
}

const Article = ({
  data: {
    allLanguages: {nodes: languages},
    allPrismicMainNavigation: navigation,
    allPrismicNotifications: notifications,
    prismicArticle,
    site: {
      siteMetadata: {lastBuildTime, siteName, siteUrl},
    },
  },
  location,
  pageContext: {translations},
}: PageProps<
  ArticleQueryQuery,
  {
    translations: Translations[];
  }
>) => {
  const {t} = useTranslation('Article');

  const title = prismicArticle.data.title.text;
  const showBreadcrumbs = prismicArticle.data.show_breadcrumbs;
  const subtitle = prismicArticle.data.subtitle.text;
  const pageTitle = prismicArticle.data.seo_title || title;
  const metaDescription = prismicArticle.data.seo_description;
  const image = prismicArticle.data.image.url && prismicArticle.data.image;
  const contentHtml = prismicArticle.data.body.html;

  const relatedItems = prismicArticle.data.related_resources
    .filter(
      (item): item is RelatedResourceWithData =>
        item.resource?.document && 'data' in item.resource.document,
    )
    .map((item, i) => (
      <ArticleSidebarListItem key={i}>
        <span
          dangerouslySetInnerHTML={{
            __html: item.resource.document.data.title.text,
          }}
        />{' '}
        <a href={item.resource.url}>{t('readMoreLabel')}</a>
      </ArticleSidebarListItem>
    ));

  return (
    <PageLayout
      languages={languages}
      lastBuildTime={parseISO(lastBuildTime)}
      lastUpdatedDate={prismicArticle.last_publication_date}
      location={location}
      navigation={navigation}
      notifications={notifications}
      siteName={siteName}
      siteUrl={siteUrl}
      translations={translations}>
      <Helmet>
        <title>{pageTitle}</title>
        {metaDescription && (
          <meta name="description" content={metaDescription} />
        )}
      </Helmet>
      <A11y title={title} />
      <PageTheme theme="white">
        <ArticleHero
          heading={title}
          subheading={subtitle}
          image={image}
          slug={location && location.pathname}
          showBreadcrumbs={showBreadcrumbs}
        />

        <PageSection noBottomPadding={false}>
          <div className={styles.wrapper}>
            <div className={styles.inner}>
              <div className={styles.content}>
                {contentHtml && (
                  <ArticleContent>
                    <div dangerouslySetInnerHTML={{__html: contentHtml}} />
                  </ArticleContent>
                )}

                {relatedItems.length > 0 && (
                  <ArticleSidebar>
                    <ArticleSidebarList heading={t('relatedResourcesHeading')}>
                      {relatedItems}
                    </ArticleSidebarList>
                  </ArticleSidebar>
                )}
              </div>
            </div>
          </div>
        </PageSection>
      </PageTheme>
    </PageLayout>
  );
};

export default withPrismicPreview(Article);

export const query = graphql`
  query ArticleQuery($uid: String!) {
    ...PageLayout

    prismicArticle(uid: {eq: $uid}) {
      _previewable
      uid
      lang
      data {
        title {
          text
        }
        show_breadcrumbs
        subtitle {
          text
        }
        body {
          html
        }
        image {
          url
          alt
        }
        seo_title
        seo_description
        related_resources {
          resource {
            url
            document {
              ... on PrismicArticle {
                data {
                  title {
                    text
                  }
                }
              }
            }
          }
        }
      }
      last_publication_date(formatString: "MM.DD.YYYY")
    }
  }
`;
